import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useData } from "../lib/DataContext";
import { Panel, TierBadge } from "./shared";

const ext = (p) => p?.production_type === "external";
const isMachine = (b, p) => !ext(p) && b.resource_type === "machine";
const field = (b, p) => (ext(p) ? "piece_rate" : isMachine(b, p) ? "setup_labor_minutes" : "production_time_minutes");
const val = (b, p) => (b[field(b, p)] > 0 ? String(b[field(b, p)]) : "");

export default function ProductionTimes() {
  const { boms, itemsById, refreshAll } = useData();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft({}); }, [boms.length]);
  const rows = [...boms].sort((a, b) => (itemsById[a.product_id]?.tier || 0) - (itemsById[b.product_id]?.tier || 0) || a.name.localeCompare(b.name));
  const pOf = (b) => itemsById[b.product_id];
  const cur = (b) => (b.id in draft ? draft[b.id] : val(b, pOf(b)));
  const changed = rows.filter((b) => b.id in draft && Number(draft[b.id] || 0) !== Number(b[field(b, pOf(b))] || 0));
  const missing = rows.filter((b) => !ext(pOf(b)) && !(Number(cur(b)) > 0)).length;

  const save = async () => {
    if (changed.some((b) => Number(draft[b.id]) < 0 || Number.isNaN(Number(draft[b.id])))) return toast.error("Times must be 0 or more");
    setSaving(true);
    try {
      await api.put("/boms/production-times", changed.map((b) => ({ id: b.id, [field(b, pOf(b))]: Number(draft[b.id] || 0) })));
      toast.success(`${changed.length} production time${changed.length > 1 ? "s" : ""} saved`);
      await refreshAll(); setDraft({});
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="font-mono text-xs text-[#9CA3AF]" data-testid="ptime-missing-count">
          {missing ? <span className="text-[#F59E0B]">{missing} internal BOM{missing > 1 ? "s have" : " has"} no production time</span> : <span className="text-[#10B981]">ALL INTERNAL BOMs TIMED</span>}
        </div>
        <button data-testid="ptime-save-all-btn" disabled={saving || !changed.length} onClick={save}
          className="ml-auto h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2 disabled:opacity-40">
          <Save className="w-4 h-4" />{saving ? "Saving…" : `Save All${changed.length ? ` (${changed.length})` : ""}`}
        </button>
      </div>
      <Panel className="divide-y divide-[#1E293B]" data-testid="ptime-table">
        {rows.map((b) => {
          const p = itemsById[b.product_id];
          const blank = !ext(p) && !(Number(cur(b)) > 0);
          return (
            <div key={b.id} data-testid={`ptime-row-${p?.sku}`} className={`flex items-center gap-3 px-4 py-3 ${blank ? "bg-[#F59E0B]/5 border-l-4 border-[#F59E0B]" : "border-l-4 border-transparent"}`}>
              <span className="hidden sm:inline">{p && <TierBadge tier={p.tier} />}</span>
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{p?.name || b.name}</div><div className="font-mono text-[11px] text-[#6B7280] truncate">{p?.sku}{ext(p) && <span className="text-[#38BDF8]"> · OUTSOURCED</span>}{isMachine(b, p) && <span className="text-[#A78BFA]"> · MACHINE · RUN {b.machine_run_minutes || 0}m</span>}<span className="hidden sm:inline"> · {b.name}</span></div></div>
              <input type="number" step="any" min="0" inputMode="decimal" data-testid={`ptime-input-${p?.sku}`} placeholder="—" value={cur(b)}
                onChange={(e) => setDraft({ ...draft, [b.id]: e.target.value })}
                className="no-spin w-24 h-12 px-3 text-right font-mono bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]" />
              <span className="hidden sm:inline font-mono text-[11px] text-[#6B7280] w-14">{ext(p) ? "$ / UNIT" : isMachine(b, p) ? "SETUP MIN" : "MIN/UNIT"}</span>
            </div>
          );
        })}
        {!rows.length && <div className="p-6 text-[#6B7280]">No BOMs yet.</div>}
      </Panel>
    </div>
  );
}
